import React, { useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { collaboratorsApi } from '../api/collaborators';
import { householdApi } from '../api/household';
import { useAuthContext } from '../contexts/AuthContext';
import { Button } from '../components/ui/Button';
import { useToast } from '../components/ui/Toast';
import { HOUSEHOLD_QUERY_KEY } from '../hooks/useHousehold';

export function AcceptInviteScreen() {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { user, isLoading: authLoading } = useAuthContext();
  const queryClient = useQueryClient();
  const [accepted, setAccepted] = useState(false);

  // New flow: ?code=XXXX  |  Legacy flow: /invite/:token
  const code = searchParams.get('code') ?? token ?? '';
  const isNewFlow = Boolean(searchParams.get('code'));

  const { data: inviteInfo, isLoading: infoLoading, error } = useQuery({
    queryKey: ['invite', code],
    queryFn: () =>
      isNewFlow
        ? collaboratorsApi.getInviteInfo(code)
        : householdApi.getInviteInfo(code),
    enabled: Boolean(code),
    staleTime: 60_000,
  });

  const legacyAccept = useMutation({
    mutationFn: () => householdApi.acceptInvite({ token: code }),
    onSuccess: (data) => {
      queryClient.setQueryData(HOUSEHOLD_QUERY_KEY, data);
    },
  });

  const newAccept = useMutation({
    mutationFn: () => collaboratorsApi.acceptInvite(code),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: HOUSEHOLD_QUERY_KEY });
    },
  });

  const isPending = legacyAccept.isPending || newAccept.isPending;

  const handleAccept = async () => {
    if (!code) return;
    try {
      if (isNewFlow) {
        await newAccept.mutateAsync();
      } else {
        await legacyAccept.mutateAsync();
      }
      setAccepted(true);
      addToast('Você entrou na lista!', 'success');
      setTimeout(() => navigate('/items'), 1500);
    } catch {
      addToast('Convite inválido ou expirado.', 'error');
    }
  };

  if (authLoading || infoLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) {
    const redirectBack = isNewFlow
      ? `/accept-invite?code=${code}`
      : `/invite/${code}`;
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-5 text-center gap-5">
        <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center">
          <svg width="32" height="32" fill="none" viewBox="0 0 24 24" stroke="#10b981" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <div>
          <p className="text-lg font-bold text-gray-900">Você precisa estar logado</p>
          <p className="text-sm text-gray-500 mt-1">Entre na sua conta para aceitar o convite.</p>
        </div>
        <Button
          variant="primary"
          onClick={() => navigate(`/login?redirect=${encodeURIComponent(redirectBack)}`)}
        >
          Entrar na conta
        </Button>
      </div>
    );
  }

  if (error || !inviteInfo) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-5 text-center gap-5">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
          <svg width="32" height="32" fill="none" viewBox="0 0 24 24" stroke="#ef4444" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
        <div>
          <p className="text-lg font-bold text-gray-900">Convite inválido</p>
          <p className="text-sm text-gray-500 mt-1">Este convite pode ter expirado ou é inválido.</p>
        </div>
        <Button variant="secondary" onClick={() => navigate('/')}>
          Ir para o início
        </Button>
      </div>
    );
  }

  if (accepted) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-5 text-center gap-5">
        <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center">
          <svg width="40" height="40" fill="none" viewBox="0 0 24 24" stroke="#10b981" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <p className="text-xl font-bold text-gray-900">Pronto! Você entrou na lista.</p>
        <p className="text-sm text-gray-500">Redirecionando...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-5 text-center gap-6">
      <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center">
        <svg width="40" height="40" fill="none" viewBox="0 0 24 24" stroke="#10b981" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      </div>

      <div>
        <p className="text-xl font-bold text-gray-900">Você foi convidado!</p>
        <p className="text-gray-600 mt-2">
          <span className="font-semibold">{inviteInfo.ownerName}</span> te convidou para a lista{' '}
          <span className="font-semibold text-emerald-600">"{inviteInfo.householdName}"</span>.
        </p>
      </div>

      <div className="w-full max-w-xs space-y-3">
        <Button
          variant="primary"
          fullWidth
          size="lg"
          onClick={handleAccept}
          loading={isPending}
        >
          Aceitar convite
        </Button>
        <Button variant="ghost" fullWidth onClick={() => navigate('/')}>
          Recusar
        </Button>
      </div>
    </div>
  );
}
